import {
  useOperatorsCsvDownloadQuery,
  usePartnerShortQuery,
} from '@common/cache';
import { paths } from '@common/types';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import { SelectChangeEvent } from '@mui/material';
import { debounce, upperCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  useOperatorTableStore,
  PersistedOperatorColumn,
  initialOperatorColumns,
} from '../Filters/filters.store';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';

export type IOperatorTableEntry =
  paths['/record/operators']['get']['responses']['200']['content']['application/json']['records'][number];

export type { IOperatorTableFilters } from '../Filters/filters.store';

const buildOperatorColumns = (
  isDense: boolean,
): GridColDef<IOperatorTableEntry>[] => [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {params.row.image ? (
          <img
            alt='operator thumbnail'
            style={{ width: 24, height: 24, marginRight: 32 }}
            src={params.row.image.thumbnail_xsmall}
          />
        ) : (
          <PermPhoneMsgIcon
            aria-label='Default operator icon'
            color='primary'
            sx={{ width: 24, height: 24, marginRight: 4, flexShrink: 0 }}
          />
        )}
        <TruncatedTextWithConditionalTooltip
          text={params.row.name}
          variant='body2'
          width='15vw'
        />
      </Box>
    ),
  },
  {
    field: 'partner_name',
    headerName: 'Partner',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.partner_name}
        variant='body2'
        width='15vw'
      />
    ),
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <TruncatedTextWithConditionalTooltip
          text={params.row.city}
          variant='body2'
          width='100%'
        />
        {!isDense && (
          <TruncatedTextWithConditionalTooltip
            variant='caption'
            color='textSecondary'
            text={params.row.state}
            width='100%'
          />
        )}
      </Box>
    ),
  },
  {
    field: 'county',
    headerName: 'County',
    minWidth: 140,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.county}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'building_count',
    headerName: 'Buildings',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.building_count}
        variant='body2'
        width='100%'
      />
    ),
  },
];

export const useOperatorTable = () => {
  const {
    filters,
    setFilters,
    clearStore,
    columns: persistedColumns,
    setColumns: setPersistedColumns,
    pinnedColumns,
    setPinnedColumns,
    hiddenColumns,
    setHiddenColumns,
    density,
    setDensity,
    resetView,
  } = useOperatorTableStore();
  const { sort_by, sort_order } = filters;

  const apiRef = useGridApiRef();

  const { data: partnerShortOptions = [] } = usePartnerShortQuery();
  const { data, isPending, mutateAsync } = useOperatorsCsvDownloadQuery();

  const [searchInputValue, setSearchInputValue] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `operators`;
      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }
      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [data]);

  useEffect(() => {
    return () => setFilters({ search: '' });
  }, []);

  const handleDownload = async () => {
    const loadingToast = toast.loading('Downloading Operators Table...', {
      isLoading: true,
      autoClose: 500,
    });
    try {
      await mutateAsync({
        sort_by: filters.sort_by,
        sort_order: upperCase(filters.sort_order) as 'ASC' | 'DESC',
        ...(filters.search !== '' && { search: filters.search }),
        ...(filters.partner_id !== '' && { partner_id: filters.partner_id }),
        ...(filters.state !== '' && { state: filters.state }),
        ...(filters.county !== '' && { county: filters.county }),
      });
      toast.success('Downloaded');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleChangeOrder = (field: string, sort: GridSortDirection) => {
    if (!field || !sort) {
      setFilters({ sort_by: 'name', sort_order: 'asc' });
      return;
    }
    setFilters({ sort_by: field as typeof sort_by, sort_order: sort });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    setFilters({ limit: model.pageSize, page: model.page + 1 });
  };

  const handleChangePartnerFilter = (e: SelectChangeEvent<string>) => {
    setFilters({ partner_id: e.target.value });
  };

  const handleChangeStateFilter = (e: SelectChangeEvent<string>) => {
    setFilters({ state: e.target.value });
  };

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setFilters({ search: value }), 500),
    [filters, setFilters],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInputValue(value);
    debouncedSetSearch(value);
  };

  const runtimeColumns = useMemo(
    () => buildOperatorColumns(density === 'dense'),
    [density],
  );
  const columns = buildGridColumns<
    IOperatorTableEntry,
    PersistedOperatorColumn
  >(runtimeColumns, persistedColumns);

  const toPersistedColumn = (
    col: GridColDef<IOperatorTableEntry>,
  ): PersistedOperatorColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    setHiddenColumns(
      columns.map(col => col.field).filter(field => model[field] === false),
    );
  };

  const MIN_VISIBLE_COLUMNS = 4;
  const visibleColumnsCount = Object.values(columnVisibilityModel).filter(
    Boolean,
  ).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;
  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  const handleResetAll = () => {
    clearStore();
    setSearchInputValue('');
    resetView();
  };

  return {
    filters,
    columns: columnsWithHideable,
    handleChangeOrder,
    onPageModelChange,
    handleSearchChange,
    handleChangePartnerFilter,
    handleChangeStateFilter,
    sort_by,
    sort_order,
    partnerShortOptions: partnerShortOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    handleDownload,
    isDownloading: isPending,
    searchInputValue,
    handleResetAll,
    initialSortFilters: { sort_order: 'asc', sort_by: 'name' },
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    density,
    handleDensityChange: setDensity,
    initialColumns: initialOperatorColumns,
    apiRef,
  };
};
